import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import { Plan, User } from './types';
import './Admin.css';

const CreateInvoice = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [paymentLink, setPaymentLink] = useState<string>('');
    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
    const [isLoadingPlans, setIsLoadingPlans] = useState<boolean>(false);
    const [isCreatingInvoice, setIsCreatingInvoice] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showUserList, setShowUserList] = useState<boolean>(false);

    // Fetch users
    useEffect(() => {
        setIsLoadingUsers(true);
        fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-users`, {
            headers: {
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }
            return response.json();
        })
        .then(data => {
            setUsers(data.users || []);
        })
        .catch(error => {
            console.error("Error fetching users:", error);
            toast.error("Failed to load users.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        })
        .finally(() => {
            setIsLoadingUsers(false);
        });
    }, []);

    // Fetch plans
    useEffect(() => {
        setIsLoadingPlans(true);
        fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/get-plans`, { // Assuming this endpoint exists
            headers: {
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Failed to fetch plans');
            }
            return response.json();
        })
        .then(data => {
            setPlans(data.plans || []); // Adjust based on actual API response structure
        })
        .catch(error => {
            console.error("Error fetching plans:", error);
            toast.error("Failed to load plans.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        })
        .finally(() => {
            setIsLoadingPlans(false);
        });
    }, []);

    const handleCreateInvoice = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selectedUserId || !selectedPlanId) {
            toast.error("Please select both a user (stockId) and a plan.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            return;
        }

        setIsCreatingInvoice(true);
        setPaymentLink(''); // Clear previous link

        try {
            // *** NB: Assuming endpoint /api/create-invoice ***
            const response = await fetch(`${process.env.REACT_APP_ADMIN_API_URL}/api/create-invoice`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({ userId: selectedUserId, planId: selectedPlanId }),
            });

            if (!response.ok) {
                toast.error(response.statusText || 'Failed to create invoice link.', { closeButton: false, hideProgressBar: true, autoClose: 3000 });
                return;
            }

            const data = await response.json();
            if (data.url) { // Adjust based on actual API response
                setPaymentLink(data.url);
                toast.success("Payment link generated successfully!", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            } else {
                 toast.error('No payment link received from server.', { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            }
        } catch (error: any) {
            console.error("Error creating invoice:", error);
            toast.error(`Error: ${error.message || 'An unknown error occurred.'}`, { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        } finally {
            setIsCreatingInvoice(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(paymentLink)
            .then(() => {
                toast.info("Link copied to clipboard!", { closeButton: false, hideProgressBar: true, autoClose: 2000 });
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
                toast.error("Failed to copy link.", { closeButton: false, hideProgressBar: true, autoClose: 3000 });
            });
    };

    // Filter users based on search term (stockId or email)
    const filteredUsers = users.filter(user =>
        user._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Handle clicks outside the user list to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
             // Check if the click target is outside the container of the search input and list
            const container = document.querySelector('.searchable-select-container');
            if (container && !container.contains(event.target as Node)) {
                setShowUserList(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []); // Empty dependency array means this effect runs once on mount

    return (
        <div className="create-invoice-container">
            <h2>Create Invoice</h2>
            <form onSubmit={handleCreateInvoice} className="invoice-form">
                {/* Searchable User/StockID selector */}
                <div className="form-group searchable-select-container">
                    <label htmlFor="stockIdSearch">User (Stock ID):</label>
                    <input
                        type="text"
                        id="stockIdSearch"
                        className="form-input"
                        placeholder={isLoadingUsers ? "Loading users..." : "Search by ID or Email..."}
                        value={searchTerm}
                        onChange={(e) => {setSearchTerm(e.target.value); setShowUserList(true); setSelectedUserId(''); setPaymentLink('')}}
                        onFocus={() => setShowUserList(true)}
                        disabled={isLoadingUsers}
                        autoComplete="off" // Prevent browser autofill interference
                    />
                    {showUserList && filteredUsers.length > 0 && (
                        <ul className="searchable-list">
                            {filteredUsers.map(user => ( // user is now { stockId: string, email?: string }
                                <li
                                    key={user._id} // Use user.stockId as key
                                    onClick={() => {
                                        setSelectedUserId(user._id); // Set selected stockId from user object
                                        // Update search term to show email (if available) and stockId
                                        setSearchTerm(`${user.email ? user.email + ' ' : ''}(${user._id})`); 
                                        setShowUserList(false);
                                        setPaymentLink('');
                                    }}
                                >
                                    {/* Display email (if available) and stockId */} 
                                    {user.email ? `${user.email} (${user._id})` : user._id}
                                </li>
                            ))}
                        </ul>
                    )}
                     {/* Display selected Stock ID */}
                     {selectedUserId && !showUserList && (
                         <p className="selected-info">Selected User ID: <strong>{selectedUserId}</strong></p>
                     )}
                </div>

                {/* Plan Selector */}
                <div className="form-group">
                    <label htmlFor="planId">Plan:</label>
                    <select
                        id="planId"
                        className="form-select"
                        value={selectedPlanId}
                        onChange={(e) => {setSelectedPlanId(e.target.value); setPaymentLink('');}}
                        disabled={isLoadingPlans || plans.length === 0}
                        required
                    >
                        <option value="" disabled>{isLoadingPlans ? "Loading plans..." : "Select a Plan"}</option>
                        {plans.map(plan => (
                            <option key={plan.planId} value={plan.planId}>
                                {plan.planId} ({plan.price / 100}$) - {plan.limits.creditsAvailable} Credits
                            </option>
                        ))}
                    </select>
                </div>

                <button type="submit" className="submit-button" disabled={isCreatingInvoice || !selectedUserId || !selectedPlanId}>
                    {isCreatingInvoice ? "Creating..." : "Create Invoice"}
                </button>
            </form>

            {/* Display Payment Link */}
            {paymentLink && (
                <div className="payment-link-container">
                    <h3>Payment Link Generated:</h3>
                    <div className="link-display">
                        <input type="text" readOnly value={paymentLink} className="form-input link-input" />
                        <button type="button" onClick={copyToClipboard} className="copy-button">Copy</button>
                    </div>
                </div>
            )}
        <ToastContainer />
        </div>
    );
};

export default CreateInvoice; 